import type { PanelTourStep } from '../components/ui/PanelTour';

export const BUILD_PANEL_TOUR: PanelTourStep[] = [
  {
    id: 'welcome',
    target: '[data-tour="build-navigation"]',
    title: 'A quick look around Build Team',
    description:
      'Follow projects, find a builder’s work, and keep schematics connected. This short tour shows you where to go. You can skip at any time.',
  },
  {
    id: 'projects',
    tab: 'projects',
    target: '[data-tour="build-projects"]',
    title: 'Start with the project',
    description:
      'Projects bring related builds together. Open a project to see progress, assignments and files. The Builds tab lets you browse individual builds.',
  },
  {
    id: 'inside-a-project',
    tab: 'projects',
    target: '[data-tour="build-projects"]',
    title: 'Inside a project or build',
    description:
      'Every project and build page has the same sections. Assignments is who is building it: build leads and admins add people with Assign builder. Schematics & screenshots holds the files, each with a title you choose. Pictures is for progress shots. The page has its own Take a tour.',
  },
  {
    id: 'people',
    tab: 'team',
    target: '[data-tour="build-people-search"]',
    title: 'Start with a person, too',
    description:
      'Search a builder by name here. Role and status filters help you narrow the roster without looking through every project.',
  },
  {
    id: 'contributions',
    tab: 'team',
    target: '[data-tour="builder-contributions"]',
    fallbackTarget: '[data-tour="build-people-search"]',
    title: 'See their contributions together',
    description:
      'Choose View contributions on a builder’s card to see active, completed and previous assignments, project links, and their uploads. Once builders are on the roster, their cards appear below the search.',
  },
  {
    id: 'schematics',
    tab: 'schematics',
    target: '[data-tour="build-files-search"]',
    title: 'Find the right file',
    description:
      'Search the shared library by title, filename or description. Screenshots preview in the list, and Edit changes a file’s title without opening a project. You can also find files beside a builder’s assignments, with links back to the build or project they belong to.',
  },
  {
    id: 'replay',
    target: '[data-tour="build-team-replay"]',
    title: 'You’re ready to explore',
    description:
      'Take a tour is always here if you want another look. Your progress and whether you finish or skip are remembered in this browser. Finishing takes you back to where you started.',
  },
];

export function buildPanelTour(canCreate: boolean, canAdmin: boolean): PanelTourStep[] {
  return [
    ...BUILD_PANEL_TOUR.slice(0, -1),
    ...(canCreate ? [{ id: 'create-project', tab: 'projects', target: '[data-tour="build-create"]', title: 'Start a project', description: 'Create a project to bring related builds together, then add assignments and schematics as work progresses.' }] : []),
    ...(canCreate ? [{ id: 'upload-files', tab: 'schematics', target: '[data-tour="build-files-upload"]', title: 'Upload to the library', description: 'Upload schematics and screenshots here and give them a title; that title, not the filename, is what everyone sees. To attach files to a project or build straight away, upload from that page instead.' }] : []),
    ...(canAdmin ? [{ id: 'manage-builders', tab: 'team', target: '[data-tour="build-add-member"]', title: 'Keep the builder roster up to date', description: 'Add builders here. Review a builder’s contributions before changing their roster status or assignments.' }] : []),
    BUILD_PANEL_TOUR[BUILD_PANEL_TOUR.length - 1],
  ];
}

/**
 * A project or build page. The same tour id on both, so finishing it on one
 * dismisses it on the other; each step opens the section it talks about.
 * Steps that need a permission only appear when the viewer has it.
 */
export function buildWorkTour(kind: 'project' | 'build', canLead: boolean, canCreate: boolean, hasPictures: boolean): PanelTourStep[] {
  const steps: (PanelTourStep | false)[] = [
    {
      id: 'sections',
      target: '[data-tour="build-work-sections"]',
      title: `Everything about this ${kind} is in these sections`,
      description:
        'People & planning, files, connections and history. On a narrow screen the list becomes a Go to section menu at the top. You can skip at any time.',
    },
    canLead
      ? {
          id: 'assign',
          query: { section: 'assignments' },
          target: '[data-tour="build-assign"]',
          title: 'Assign builders here',
          description:
            'Assign builder opens the form: search a staff member by username and choose their role. They need to be on the build team roster first; a build admin adds them under Build Team → Team. Change a role or mark an assignment completed from the list.',
        }
      : {
          id: 'assignments-read',
          query: { section: 'assignments' },
          target: '[data-tour="build-work-sections"]',
          title: 'Who is building it',
          description:
            'Assignments shows who is on this work and in what role. Only build leads and admins can assign or remove builders, so ask a lead if someone needs adding.',
        },
    {
      id: 'schematics',
      query: { section: 'schematics' },
      target: canCreate ? '[data-tour="build-schematic-upload"]' : '[data-tour="build-work-sections"]',
      fallbackTarget: '[data-tour="build-work-sections"]',
      title: 'Schematics and screenshots, with a title',
      description: canCreate
        ? 'Upload .schem files and screenshots here and give each a title; that title, not the filename, is what everyone sees. Screenshots preview in the list. Edit changes the title later, Unlink removes a file from this page, and Delete (build admins) removes it everywhere.'
        : 'The files for this work, listed by the title the uploader gave them. Screenshots preview in the list and schematics download from here.',
    },
    hasPictures && {
      id: 'gallery',
      query: { section: 'gallery' },
      target: canCreate ? '[data-tour="build-gallery-upload"]' : '[data-tour="build-work-sections"]',
      fallbackTarget: '[data-tour="build-work-sections"]',
      title: 'Pictures of the build',
      description: canCreate
        ? 'Progress shots and references go here with Add pictures. Images attached to comments appear here too, so the whole project can be seen in one place.'
        : 'Progress shots and references for the project, including images attached to comments.',
    },
    {
      id: 'replay',
      target: '[data-tour="build-work-replay"]',
      title: 'Take a tour is always here',
      description:
        'Replay this guide whenever you need it. Progress is remembered in this browser, and finishing takes you back to the section you were on.',
    },
  ];
  return steps.filter((step): step is PanelTourStep => Boolean(step));
}

export function eventPanelTour(canLead: boolean, canPoints = false, canAdmin = false): PanelTourStep[] {
  return [
    { id: 'feedback-forms', target: '[data-tour="event-feedback"]', title: 'Keep listening after the event', description: 'Forms & feedback opens the shared builder. For an event, open its Feedback forms section to create a linked form, collect answers after completion, and optionally award each player one point. The builder has its own guided tour.' },
    {
      id: 'welcome',
      target: '[data-tour="event-navigation"]',
      title: 'A quick look around Event Team',
      description:
        'Plan what’s next, find your people, and check what happened. We’ll point out the everyday tools first. Skip is available on every step.',
    },
    {
      id: 'events',
      target: '[data-tour="event-events"]',
      title: 'Find an event or its date',
      description:
        'Events holds the event list and progress. Calendar shows the schedule, and Team opens the roster. You can switch between them from here.',
    },
    ...(canLead
      ? [
          {
            id: 'players',
            tab: 'clearance',
            target: '[data-tour="event-command-players"]',
            title: 'Check one player or a whole group',
            description:
              'Quick search starts with who you want to check. Search players by name or UUID, or add a saved group. Overlapping groups count each player only once.',
          },
          {
            id: 'server',
            tab: 'clearance',
            target: '[data-tour="event-command-server"]',
            title: 'Choose the event server',
            description:
              'Select a suggested server or type its address. The search is limited to that server, so activity elsewhere won’t get mixed in.',
          },
          {
            id: 'time',
            tab: 'clearance',
            target: '[data-tour="event-command-time"]',
            title: 'Set the time window',
            description:
              'Use a quick time range or enter From and To. Times use the timezone shown below the controls. Specific command names are optional; leaving them blank searches all commands.',
          },
          {
            id: 'search',
            tab: 'clearance',
            target: '[data-tour="event-command-run"]',
            title: 'See the command evidence',
            description:
              'Search shows each player’s command counts and recent entries. Use Saved checks for team verdicts and rewards: one member using a prohibited command fails the whole team. The tour won’t run a search.',
          },
        ]
      : [
          {
            id: 'checks',
            tab: 'clearance',
            target: '[data-tour="event-command-views"]',
            title: 'Revisit command checks',
            description:
              'Saved checks and player groups live here. Teams are judged together: one member using a prohibited command fails the whole team. Open a check to review the team verdict and command evidence.',
          },
        ]),
    {
      id: 'player-rewards',
      tab: 'points',
      query: { reward_view: 'player' },
      target: '[data-tour="event-reward-player"]',
      title: 'Rewards belong to players',
      description: 'Search any player to see their event points, points history and prefix rewards. These points recognise participation in events across the network.',
    },
    ...(canPoints ? [{
      id: 'award-player-points', tab: 'points', target: '[data-tour="event-reward-award"]',
      title: 'Reward participation your way',
      description: 'Award points to any player, choose an amount and a reason, and optionally link an event. You can also award directly from an event’s participants. Prefix rewards holds thresholds and grants.',
    }] : []),
    ...(canPoints ? [{
      id: 'event-check-prefix-rewards', target: '[data-tour="event-events"]',
      title: 'Give an event its own reward',
      description: 'Open an event and choose Prefix reward under Players & rewards. Run and save a linked command check, then reward the passing teams. One member cheating fails the whole team, so none of its members receive the prefix. Existing holders are skipped and no points are spent.',
    }] : []),
    ...(canAdmin ? [{
      id: 'manage-event-tags', tab: 'tags', target: '[data-tour="event-more"]',
      title: 'Keep event tags organised', description: 'Your management access includes Tags in More tools, where you can maintain the labels used to find and organise events.',
    }] : []),
    {
      id: 'more',
      target: '[data-tour="event-more"]',
      title: 'Everything else is still close by',
      description:
        'More tools holds ideas, projects, workflows and the archive. Less frequent setup stays out of your daily path.',
    },
    {
      id: 'replay',
      target: '[data-tour="event-team-replay"]',
      title: 'Come back whenever you need',
      description:
        'Use Take a tour to replay this guide. Your progress, skips and completion stay in this browser. Finishing returns you to where you started.',
    },
  ];
}

export function partnershipPanelTour(canCreate: boolean, canAdmin: boolean): PanelTourStep[] {
  return [
    { id: 'welcome', target: '[data-tour="pr-navigation"]', title: 'Your partnerships, in one place', description: 'Find an existing relationship, pick up your assigned outreach, or start with a creator or modpack. You can skip this tour at any time.' },
    { id: 'find-partnership', tab: 'all', target: '[data-tour="pr-search"]', title: 'Find the relationship', description: 'Search partnerships by title. Use status to narrow the list; extra filters are available when you need them. Open a partnership to see its contacts, assignments and progress.' },
    { id: 'my-partnerships', tab: 'mine', target: '[data-tour="pr-mine"]', title: 'Pick up your work', description: 'My partnerships keeps the relationships assigned to you together. Prospects brings potential partnerships into a separate list.' },
    { id: 'creators', tab: 'creators', target: '[data-tour="pr-creators-search"]', title: 'Start with a creator', description: 'Search by name and open a creator to see their contacts, linked modpacks and partnerships together.' },
    { id: 'modpacks', tab: 'modpacks', target: '[data-tour="pr-modpacks-search"]', title: 'Start with a modpack', description: 'Find a modpack by name or platform. Its detail view connects you to its creators and partnership history.' },
    { id: 'partner-page', tab: 'all', target: '[data-tour="pr-partner-page"]', fallbackTarget: '[data-tour="pr-navigation"]', title: 'Each partner team has a page', description: 'Partner page, beside each creator, holds their team and tiers, the Discord channel Mr. Roboto keeps for them, the updates they report, their packs and the server packs they have shared.' },
    { id: 'partner-updates', tab: 'updates', target: '[data-tour="pr-updates"]', title: 'Updates partners tell us about', description: 'Partners report new versions from their Discord channel, often before CurseForge lists them. Versions Argus finds on CurseForge that nobody reported land here too. Move each one from new to live; the partner sees the progress on their card in Discord.' },
    { id: 'partner-discord', tab: 'discord', target: '[data-tour="pr-discord"]', title: 'The Discord side at a glance', description: 'Partner Discord shows whether Mr. Roboto can hand out the partner roles and use the Partnerships category, channels there that are not linked to a partner yet, and people holding a partner role who are not on any team.' },
    ...(canCreate ? [{ id: 'create-partnership', tab: 'all', target: '[data-tour="pr-create"]', title: 'Add what you’re working on', description: 'Create a partnership here. In Creators or Modpacks this action changes to add that kind of record, keeping the tools relevant to your current view.' }] : []),
    ...(canAdmin ? [{ id: 'manage-tags', target: '[data-tour="pr-tags"]', title: 'Organise the shared labels', description: 'Your management access lets you maintain the tags used to organise partnerships.' }] : []),
    { id: 'replay', target: '[data-tour="partnerships-replay"]', title: 'Help stays close by', description: 'Replay the tour here whenever you need it. This browser remembers viewed and skipped features. If your access expands, only newly available steps appear automatically.' },
  ];
}

/**
 * A partner's own page. Each step opens the section it talks about. Editing steps
 * point at the controls when the viewer can use them, and at the section list otherwise.
 */
export function partnerPageTour(canEdit: boolean): PanelTourStep[] {
  const sections = '[data-tour="partner-sections"]';
  return [
    {
      id: 'sections',
      target: sections,
      title: 'Everything about this partner',
      description:
        'Their team, Discord channel, update reports, packs, server packs and history, one section each. You can skip at any time.',
    },
    {
      id: 'team',
      query: { section: 'team' },
      target: canEdit ? '[data-tour="partner-add-member"]' : sections,
      fallbackTarget: sections,
      title: 'Their team and tiers',
      description: canEdit
        ? 'Add each person by Discord name and choose a tier. Partnered Developers get the developer role and can share server packs with us. Contributors get the acknowledgement role. Everyone on the team can see the partner channel.'
        : 'Who is on the team and at which tier. Partnered Developers get the developer role and can share server packs with us; Contributors get the acknowledgement role.',
    },
    {
      id: 'discord',
      query: { section: 'discord' },
      target: canEdit ? '[data-tour="partner-discord-managed"]' : sections,
      fallbackTarget: sections,
      title: 'Mr. Roboto looks after Discord',
      description: canEdit
        ? 'Link their existing channel, or let Mr. Roboto create one under Partnerships. Turning management on shows a preview first, so you see exactly what will change before anything does.'
        : 'Whether Mr. Roboto manages their channel and roles, and when it last checked.',
    },
    {
      id: 'updates',
      query: { section: 'updates' },
      target: sections,
      title: 'Their updates, from reported to live',
      description:
        'Versions they report from Discord appear here. Set the status as it moves along, and add a note if they should see it. A report goes live on its own once our servers run that version.',
    },
    {
      id: 'packs',
      query: { section: 'packs' },
      target: sections,
      title: 'Partner, CurseForge and our servers side by side',
      description: canEdit
        ? 'Link each pack to its Apogee entry to compare what they reported, what CurseForge lists and what our servers run.'
        : 'What they reported, what CurseForge lists and what our servers run, for each linked pack.',
    },
    {
      id: 'replay',
      target: '[data-tour="partner-page-replay"]',
      title: 'Take a tour is always here',
      description:
        'Replay this guide whenever you need it. Progress is remembered in this browser, and finishing takes you back to the section you were on.',
    },
  ];
}

export function logbookPanelTour(canProtect: boolean, canExport: boolean, canAdminister: boolean): PanelTourStep[] {
  return [
    {
      id: 'welcome',
      target: '[data-tour="logbook-filters"]',
      title: 'How Logbook works',
      description:
        'Every pack running Logbook records what players do on the server. A search here is a question: who, when, where, and what kind of action. This tour shows how to ask one and how to read the answer. You can skip at any time.',
    },
    {
      id: 'question',
      target: '[data-tour="logbook-filter-row"]',
      title: 'Start with a name and a window',
      description:
        'Type a player name and choose how far back to look. Narrow to particular servers or actions when you need to. Results update as you change the filters; they combine, so an exact value that matches nothing empties the result.',
    },
    {
      id: 'presets',
      target: '[data-tour="logbook-presets"]',
      title: 'Looking for something common',
      description:
        'These presets select the actions behind everyday reports: griefing, item movement, land claims, trades. One click applies a set, a second click clears it.',
    },
    {
      id: 'more-filters',
      target: '[data-tour="logbook-more-filters"]',
      title: 'Narrow to a place or a thing',
      description:
        'More filters adds a region around pasted coordinates, an exact player UUID, an outcome, a dimension, an object id and a mod namespace. Region is how a griefed base becomes a search.',
    },
    {
      id: 'totals',
      target: '[data-tour="logbook-totals"]',
      title: 'The counts cover everything',
      description:
        'The tiles count every record that matches, across the whole window, not only the page loaded below. A capture gap means the server failed to record something, so evidence there is incomplete.',
    },
    {
      id: 'activity',
      target: '[data-tour="logbook-activity"]',
      title: 'When it happened',
      description:
        'The chart shows matching records over time. Drag across a spike to narrow the window to those minutes.',
    },
    {
      id: 'breakdowns',
      target: '[data-tour="logbook-breakdowns"]',
      title: 'Who did the most of it',
      description:
        'Breakdowns rank the matching records by action, and by actor, object, server, dimension or outcome. Click a row to filter to it.',
    },
    {
      id: 'evidence',
      target: '[data-tour="logbook-evidence"]',
      title: 'Reading a record',
      description:
        'Each row is one record: who did what to what, where and when. Expand it for the full capture, the before and after state, and what it does not prove. Repeats by the same player fold into one run; open the run to see each record.',
    },
    {
      id: 'view-bar',
      target: '[data-tour="logbook-view-bar"]',
      title: 'Hidden noise',
      description:
        'Position samples and other high-volume records are hidden by default, with a count of what is hidden. Click a class to show it. The grouping window controls how repeats fold together.',
    },
    {
      id: 'coverage',
      target: '[data-tour="logbook-coverage"]',
      fallbackTarget: '[data-tour="logbook-evidence"]',
      title: 'What the answer does not cover',
      description:
        'Coverage lists the servers searched and how fresh their evidence is. A server can still hold records it has not sent yet, and records inserted after the page opened are excluded until you search again.',
    },
    canProtect
      ? {
          id: 'cases',
          target: '[data-tour="logbook-cases"]',
          title: 'Keep evidence in a case',
          description:
            'Add to a case copies a record into Argus permanently, so a ban keeps its evidence after Logbook retention removes the original. Cases holds those files and links them to notes, warnings and bans.',
        }
      : {
          id: 'cases-read',
          target: '[data-tour="logbook-cases"]',
          title: 'Cases',
          description:
            'Cases are the evidence files moderators build from these records. Each keeps its own permanent copy, linked to the note, warning or ban it justified.',
        },
    {
      id: 'nearby',
      target: '[data-tour="logbook-nearby"]',
      title: 'Who was here',
      description:
        'Give it a server, a position, a radius and a window. It ranks everyone recorded there by how much evidence they left, with when each was first and last seen.',
    },
    ...(canExport ? [{
      id: 'export',
      target: '[data-tour="logbook-export"]',
      title: 'Export the result',
      description: 'Export downloads the current result as one page of NDJSON. Narrow the filters if more records match than one page holds.',
    }] : []),
    ...(canAdminister ? [{
      id: 'health',
      target: '[data-tour="logbook-health"]',
      title: 'Check the evidence itself',
      description: 'Evidence health shows whether Logbook is reachable, whether each server is still sending records, and how much history retention keeps.',
    }] : []),
    {
      id: 'replay',
      target: '[data-tour="logbook-replay"]',
      title: 'Take the tour again any time',
      description:
        'Take a tour replays this guide. Progress is remembered in this browser, and only steps for newly granted access appear on their own.',
    },
  ];
}

/**
 * Modpack Configs. Steps that need a permission only appear when the viewer
 * has it, so nobody is walked to a button they cannot press.
 *
 * The cluster-level targets exist only once the list has rendered a cluster,
 * so they fall back to the list itself rather than leaving the tour pointing
 * at nothing.
 */
export function modpackConfigsPanelTour(canWrite: boolean, canAdmin: boolean): PanelTourStep[] {
  const steps: (PanelTourStep | false)[] = [
    {
      id: 'welcome',
      target: '[data-tour="configs-environment"]',
      title: 'A quick look around Modpack Configs',
      description:
        'Every modpack server on the network lives here: its address, ports, cluster and pack version. This is the same data Mr. Roboto edits, now editable without a slash command. You can skip at any time.',
    },
    {
      id: 'search',
      target: '[data-tour="configs-search"]',
      title: 'Find a server',
      description:
        'Search by address, pack name, cluster, author or AMP instance. The filters narrow by cluster, region, modloader, or by what the network says right now: online, offline, in maintenance, coming soon, or hidden from players.',
    },
    {
      id: 'clusters',
      target: '[data-tour="configs-list"]',
      title: 'Grouped by cluster',
      description:
        'Servers running the same pack sit together, head first. The head is the server the Discord role and forum are keyed on. Each server shows whether players can reach it and when its world was last wiped — the same readings as the status page, which every cluster links to. A cluster whose members disagree on version is flagged; that usually means an update did not finish.',
    },
    canWrite && {
      id: 'wipe',
      target: '[data-tour="configs-wipe"]',
      fallbackTarget: '[data-tour="configs-list"]',
      title: 'Record a world wipe',
      description:
        'Players are told when a pack\'s world was last wiped. Record it here for the whole cluster at once, right after doing it. It is a date, not a status — a pack wiped a year ago is just a pack with a year-old world.',
    },
    canWrite && {
      id: 'update',
      target: '[data-tour="configs-update"]',
      fallbackTarget: '[data-tour="configs-list"]',
      title: 'Update a pack in one step',
      description:
        'Update the files on every server first — Argus cannot do that part. Then press this: tick each server to confirm, enter the version, add a changelog if there is one, and it records the version everywhere and announces it once. If any server refuses, nothing is announced.',
    },
    canWrite && {
      id: 'launch',
      target: '[data-tour="configs-launch"]',
      title: 'Launch a new pack',
      description:
        'Four short steps instead of six Discord modals: the pack, its network details, its cluster, and the catalogue ids. The RCON port fills itself in from the query port, and a new pack heads its own cluster unless you pick one to join.',
    },
    canAdmin && {
      id: 'announce-launch',
      target: '[data-tour="configs-announce-launch"]',
      fallbackTarget: '[data-tour="configs-list"]',
      title: 'Announce a launch',
      description:
        'Post the launch embed for a pack without opening it. Giving a new pack its Discord forum, and announcing it, are the last steps of a launch and can each be done from here or from the pack’s own page.',
    },
    canAdmin && {
      id: 'environment',
      target: '[data-tour="configs-environment"]',
      title: 'Production and development',
      description:
        'The Development switch shows the dev table, which is a copy and does not affect live servers. From there you can replace it wholesale with a fresh copy of production.',
    },
    {
      id: 'detail',
      target: '[data-tour="configs-list"]',
      title: 'Open a server for everything else',
      description:
        'Any row opens the full configuration, the cluster with the option to change its head, and the connections: the pack catalogue, Discord, and the live status with maintenance controls for this server or its whole cluster, including marking a new server coming soon with an optional release countdown. Hiding a pack from players is a switch at the top of that page.',
    },
  ];
  return steps.filter((step): step is PanelTourStep => Boolean(step));
}

export function scheduledCommandsPanelTour(canWrite: boolean): PanelTourStep[] {
  const steps: (PanelTourStep | false)[] = [
    {
      id: 'welcome',
      target: '[data-tour="schedules-timeline"]',
      title: 'A quick look around Scheduled Commands',
      description:
        'These are the console commands Mr. Roboto sends to servers on a timer. The strip shows when the next runs fall. The bot spaces schedules out by itself, and ticks that land within 30 seconds of each other turn amber. You can skip at any time.',
    },
    {
      id: 'filters',
      target: '[data-tour="schedules-filters"]',
      title: 'Find a schedule',
      description:
        'Search matches names, the command itself and targets. The tiles below narrow the list to what is active, paused, waiting for its start date, or failing.',
    },
    {
      id: 'table',
      target: '[data-tour="schedules-table"]',
      fallbackTarget: '[data-tour="schedules-timeline"]',
      title: 'Every schedule, and how its last run went',
      description: canWrite
        ? 'The switch pauses and resumes a schedule. Tick rows to pause, resume or delete several at once. Each row can run now, go to one player, or be duplicated. Open a row to edit it and see its run history.'
        : 'Last run shows how many servers ran the command and when. Open a row to see its full run history.',
    },
    canWrite && {
      id: 'new',
      target: '[data-tour="schedules-new"]',
      title: 'New schedules',
      description:
        'Pick the servers, how often, and optionally when it starts and stops. For announcements, describe the message to the assistant and it writes the tellraw, previewed as players will see it and compiled for the Minecraft versions you choose.',
    },
  ];
  return steps.filter((step): step is PanelTourStep => Boolean(step));
}
